import React, { useState, useEffect } from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import { useAuth } from '../context/AuthContext';
import { clinicAPI, messageAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { Users, MessageCircle, TrendingUp, Star, BarChart3 } from 'lucide-react';
import CasesPanel from '../components/CasesPanel';

const ClinicDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, messagesRes] = await Promise.all([
        clinicAPI.getProfile(user.id),
        messageAPI.getMessages(user.id),
      ]);
      setProfile(profileRes.data);
      setMessages(messagesRes.data.messages || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-primary/5" data-testid="clinic-dashboard">
      <div className="container-custom section">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-secondary mb-2">Clinic Dashboard</h1>
          {profile && <p className="text-foreground/70 text-lg">{profile.clinic_name}</p>}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="stat-card" data-testid="stat-patient-leads">
            <Users className="w-8 h-8 text-primary mx-auto mb-2" />
            <div className="text-3xl font-bold text-secondary mb-1">0</div>
            <div className="text-sm text-foreground/70">Patient Leads</div>
          </div>
          <div className="stat-card" data-testid="stat-messages">
            <MessageCircle className="w-8 h-8 text-primary mx-auto mb-2" />
            <div className="text-3xl font-bold text-secondary mb-1">{messages.length}</div>
            <div className="text-sm text-foreground/70">Messages</div>
          </div>
          <div className="stat-card" data-testid="stat-rating">
            <Star className="w-8 h-8 text-primary mx-auto mb-2" />
            <div className="text-3xl font-bold text-secondary mb-1">-</div>
            <div className="text-sm text-foreground/70">Rating</div>
            <div className="text-xs text-foreground/50 mt-1">Coming soon</div>
          </div>
          <div className="stat-card" data-testid="stat-views">
            <TrendingUp className="w-8 h-8 text-primary mx-auto mb-2" />
            <div className="text-3xl font-bold text-secondary mb-1">0</div>
            <div className="text-sm text-foreground/70">Profile Views</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Clinic Profile */}
            {profile && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="clinic-profile-card">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-xl font-semibold text-secondary">Clinic Profile</h2>
                  {profile.verified && <span className="verified-badge">Verified by TreatX</span>}
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-foreground/70">Clinic Name:</span>
                    <span className="font-medium text-secondary">{profile.clinic_name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-foreground/70">Country:</span>
                    <span className="font-medium text-secondary">{profile.country}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-foreground/70">Verification:</span>
                    <span className="font-medium text-secondary">Pending</span>
                  </div>
                  <div className="py-2">
                    <span className="text-foreground/70 block mb-2">Specialties:</span>
                    <div className="flex flex-wrap gap-2">
                      {profile.specialties?.map((specialty, idx) => (
                        <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Messages */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="messages-card">
              <h2 className="text-xl font-semibold text-secondary mb-4">Recent Messages</h2>
              {messages.length > 0 ? (
                <div className="space-y-4">
                  {messages.slice(0, 5).map((message) => (
                    <div key={message.id} className="border border-slate-200 rounded-lg p-4" data-testid={`message-${message.id}`}>
                      <p className="text-sm text-foreground/80">{message.message}</p>
                      <p className="text-xs text-foreground/50 mt-2">{formatDateLocale(message.created_at)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-foreground/60 text-center py-8">No messages yet</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="quick-actions-card">
              <h2 className="text-lg font-semibold text-secondary mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button className="w-full btn-primary" data-testid="update-profile-button">
                  Update Profile
                </button>
                <button onClick={() => navigate('/clinic/analytics/scorecards')} className="w-full btn-outline flex items-center justify-center gap-2" data-testid="view-performance-button">
                  <BarChart3 className="w-4 h-4" /> Performance
                </button>
                <button onClick={() => navigate('/clinic/offer-templates')} className="w-full btn-outline flex items-center justify-center gap-2" data-testid="view-templates-button">
                  <BarChart3 className="w-4 h-4" /> Offer Templates
                </button>
                <button className="w-full btn-outline" data-testid="view-analytics-button">
                  View Analytics
                </button>
              </div>
            </div>

            {/* Support */}
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6" data-testid="support-card">
              <h3 className="font-semibold text-secondary mb-2">Need Help?</h3>
              <p className="text-sm text-foreground/70 mb-4">Contact our clinic support team</p>
              <a href="mailto:clinics@treatx.health" className="text-sm text-primary hover:underline" data-testid="support-email">
                clinics@treatx.health
              </a>
            </div>
          </div>
        </div>

        {/* Treatment Cases — Phase 3 */}
        <div className="mt-8" data-testid="clinic-cases-section">
          <CasesPanel clinicId={user?.clinic_id} title="Clinic Cases" />
        </div>
      </div>
    </div>
  );
};

export default ClinicDashboard;